import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type * as React from "react";
import type { Omittable } from "../../lib/props";
import {
  HOVER_REVEAL_ATTR,
  REVEAL_ON_TOUCH_ATTR,
  beginHold,
  pressArmed,
} from "../../lib/touch-arm";
import { cn } from "../../lib/utils";
import {
  BOX_ALIGN,
  BOX_ALIGN_SELF,
  BOX_BACKGROUND,
  BOX_BORDER_COLOR,
  BOX_BORDER_STYLE,
  BOX_BORDER_WIDTH,
  BOX_BUTTON_RESET,
  BOX_COLUMNS,
  BOX_CURSOR,
  BOX_DURATION,
  BOX_FILL,
  BOX_FLEX,
  BOX_GAP,
  BOX_GAP_X,
  BOX_GAP_Y,
  BOX_GROUP,
  BOX_GROW_TOGGLE,
  BOX_HOVER_BACKGROUND_TOGGLE,
  BOX_HOVER_REVEAL,
  BOX_HOVER_REVEAL_DISPLAY,
  BOX_JUSTIFY,
  BOX_MARGIN,
  BOX_MARGIN_BOTTOM,
  BOX_MARGIN_BOTTOM_NEGATIVE,
  BOX_MARGIN_LEFT,
  BOX_MARGIN_LEFT_NEGATIVE,
  BOX_MARGIN_RIGHT,
  BOX_MARGIN_RIGHT_NEGATIVE,
  BOX_MARGIN_TOP,
  BOX_MARGIN_TOP_NEGATIVE,
  BOX_MARGIN_X,
  BOX_MARGIN_X_NEGATIVE,
  BOX_MARGIN_Y,
  BOX_MARGIN_Y_NEGATIVE,
  BOX_OPACITY,
  BOX_OVERFLOW,
  BOX_OVERFLOW_X,
  BOX_OVERFLOW_Y,
  BOX_PADDING,
  BOX_PADDING_BOTTOM,
  BOX_PADDING_LEFT,
  BOX_PADDING_RIGHT,
  BOX_PADDING_TOP,
  BOX_PADDING_X,
  BOX_PADDING_Y,
  BOX_POINTER_EVENTS,
  BOX_POSITION,
  BOX_REVEAL_ON_TOUCH,
  BOX_SCROLLBAR,
  BOX_SHADOW,
  BOX_SHRINK,
  BOX_SNAP_ALIGN,
  BOX_SNAP_TYPE,
  BOX_SURFACE,
  BOX_SURFACE_SHADOW,
  BOX_TOUCH_ACTION,
  BOX_TRANSITION,
  BOX_USER_SELECT,
  BOX_WRAP,
  BOX_FADE,
  PRESS_SURFACE_CLASS,
  type BackgroundToken,
  type BorderColorToken,
  type BorderEdge,
  type BorderStyleToken,
  type BorderWidthToken,
  type ColumnsToken,
  type CursorToken,
  type DisplayToken,
  type DurationToken,
  type FillOption,
  type FlexAlign,
  type FlexAlignSelf,
  type FlexDirection,
  type FlexJustify,
  type FlexToken,
  type GroupScope,
  type MarginToken,
  type NegativeSpacingToken,
  type OpacityToken,
  type OverflowToken,
  type PointerEventsToken,
  type PositionToken,
  type Responsive,
  type RoundedSide,
  type RoundedToken,
  type ScrollbarToken,
  type ShadowToken,
  type SnapAlignToken,
  type SnapTypeToken,
  type SpacingToken,
  type SurfaceToken,
  type TouchActionToken,
  type TransitionToken,
  type UserSelectToken,
  type FadeToken,
  BOX_SELECTED, BOX_DIMMED,
} from "./box-classes";
import {
  at, filterResponsive, isBreakpointObject, isRawNumber, isToken,
  resolve, resolveBoolean, resolveBorder, resolveDisplay, resolveHidden,
  resolveRounded, resolveStyleVars, resolveWith,
} from "./box-responsive";
import { BoxResizeSeam, type BoxResizeProps } from "./box-resize";

export type { ResizeEdge, ResizeSize } from "./box-resize";

// Box's prop vocabulary is public API: consumers that thread a Box prop
// through their own props (e.g. a content-padding passthrough) import the
// token types from here rather than re-declaring them.
export type {
  BackgroundToken, RoundedToken, SpacingToken, SurfaceToken,
} from "./box-classes";
// The value list behind `SurfaceToken`, for consumers that enumerate the
// surfaces (a picker, a docs matrix) rather than name one.
export { SURFACES } from "./box-classes";
// The breakpoint vocabulary is public for the same reason: a component that
// forwards a Box prop must be able to name its responsive form.
export type { Breakpoint, Responsive } from "./box-classes";

export type BoxElement =
  | "div" | "span" | "section" | "article" | "aside" | "nav" | "header"
  | "footer" | "main" | "ul" | "ol" | "li" | "form" | "label" | "button" | "a"
  | "iframe" | "pre";

/**
 * Every style prop takes a scalar OR `{ base, sm, md, lg, xl, 2xl }`.
 *
 * The breakpoints are Tailwind's own, so `md` here is the `md:` prefix there,
 * and `base` is the unprefixed utility. An unspecified breakpoint inherits the
 * nearest one below it.
 *
 *     <Box columns={{ base: 1, md: 2, xl: 3 }} padding={{ base: "sm", md: "lg" }} />
 *
 * `group`, `armOnHold`, `hoverReveal`, `revealOnTouch` and the resize props stay scalar:
 * they are identity and behaviour, not style, and a breakpoint has no meaning
 * for them.
 */
interface BoxOwnProps extends BoxResizeProps {
  // Flex (any of these opts the Box into display:flex; column is the default
  // direction once opted in — a bare Box stays display:block).
  direction?: Responsive<FlexDirection>;
  align?: Responsive<FlexAlign>;
  alignSelf?: Responsive<FlexAlignSelf>;
  justify?: Responsive<FlexJustify>;
  wrap?: Responsive<boolean>;
  gap?: Responsive<SpacingToken | number>;
  gapX?: Responsive<SpacingToken | number>;
  gapY?: Responsive<SpacingToken | number>;
  // Flex-CHILD props. They describe how this Box behaves INSIDE its parent and
  // do nothing unless that parent is a flex container.
  flex?: Responsive<FlexToken>;
  grow?: Responsive<boolean>;
  shrink?: Responsive<boolean>;
  /** Explicit display. Only needed for grid/contents/forcing block back on. */
  display?: Responsive<DisplayToken>;
  /** grid-template-columns. Implies display:grid. A count is fixed per
   *  viewport breakpoint; `fill` fits as many 10rem-minimum tracks as the
   *  Box's OWN width holds — the choice for a grid inside a pane whose width
   *  the viewport does not predict. */
  columns?: Responsive<ColumnsToken>;
  // Spacing
  padding?: Responsive<SpacingToken>;
  paddingX?: Responsive<SpacingToken>;
  paddingY?: Responsive<SpacingToken>;
  paddingTop?: Responsive<SpacingToken>;
  paddingBottom?: Responsive<SpacingToken>;
  paddingLeft?: Responsive<SpacingToken>;
  paddingRight?: Responsive<SpacingToken>;
  margin?: Responsive<MarginToken>;
  marginX?: Responsive<MarginToken>;
  marginY?: Responsive<MarginToken>;
  marginTop?: Responsive<MarginToken>;
  marginBottom?: Responsive<MarginToken>;
  marginLeft?: Responsive<MarginToken>;
  marginRight?: Responsive<MarginToken>;
  /** Negative margins — pull a child back out of its parent's padding. */
  marginXNegative?: Responsive<NegativeSpacingToken>;
  marginYNegative?: Responsive<NegativeSpacingToken>;
  marginTopNegative?: Responsive<NegativeSpacingToken>;
  marginBottomNegative?: Responsive<NegativeSpacingToken>;
  marginLeftNegative?: Responsive<NegativeSpacingToken>;
  marginRightNegative?: Responsive<NegativeSpacingToken>;
  // Sizing. A scalar rides inline `style`; a breakpoint object rides CSS
  // custom properties, because inline styles carry no media queries.
  width?: Responsive<number | string>;
  height?: Responsive<number | string>;
  minWidth?: Responsive<number | string>;
  minHeight?: Responsive<number | string>;
  maxWidth?: Responsive<number | string>;
  maxHeight?: Responsive<number | string>;
  fill?: Responsive<FillOption>;
  // Appearance
  background?: Responsive<BackgroundToken>;
  /**
   * Washes this Box on hover. The wash is the foreground color at low alpha, so it composites
   * against whatever fill this Box already has — there is no surface to name and
   * nothing to keep in sync when the fill changes.
   */
  hoverBackground?: Responsive<boolean>;
  /**
   * The row is selected: it wears the selected wash the table row and the
   * picker option wear, and a deeper one under the pointer, so a selection
   * reads the same in every list.
   */
  selected?: boolean;
  /**
   * The row is present but inactive — a paused automation, a former member:
   * the whole row fades to 40% and eases there, so the state reads without a
   * badge. Content that must stay legible stands outside the dimmed Box.
   */
  dimmed?: boolean;
  rounded?: Responsive<RoundedToken>;
  /**
   * Limits `rounded` to the named side(s); the other corners stay square. For a
   * panel that butts flush against a neighbour, so the shared seam reads as one
   * straight line.
   */
  roundedSide?: Responsive<RoundedSide | RoundedSide[]>;
  border?: Responsive<boolean | BorderEdge | BorderEdge[]>;
  borderColor?: Responsive<BorderColorToken>;
  borderStyle?: Responsive<BorderStyleToken>;
  borderWidth?: Responsive<BorderWidthToken>;
  shadow?: Responsive<ShadowToken>;
  opacity?: Responsive<OpacityToken>;
  /**
   * Which surface this Box paints: `sunken` for a recessed well, `background`
   * for the page, `card` for a panel on it, `popover` for anything portalled,
   * `sidebar` for the navigation region beside the page. Applies that
   * surface's fill, hairline color and shadow, and wins over
   * `background`/`shadow` on the same Box.
   */
  surface?: Responsive<SurfaceToken>;
  /** Shadow from a different surface, for a Box whose fill and shadow come
   *  from different surfaces. */
  surfaceShadow?: Responsive<SurfaceToken>;
  // Layout
  position?: Responsive<PositionToken>;
  inset?: Responsive<number | string>;
  insetX?: Responsive<number | string>;
  insetY?: Responsive<number | string>;
  top?: Responsive<number | string>;
  right?: Responsive<number | string>;
  bottom?: Responsive<number | string>;
  left?: Responsive<number | string>;
  zIndex?: Responsive<number>;
  overflow?: Responsive<OverflowToken>;
  overflowX?: Responsive<OverflowToken>;
  overflowY?: Responsive<OverflowToken>;
  /** Scrollbar presentation of this scroller. `hidden` when an edge fade or
   * peeking content already signals overflow. */
  scrollbar?: Responsive<ScrollbarToken>;
  hidden?: Responsive<boolean>;
  cursor?: Responsive<CursorToken>;
  // Interaction
  pointerEvents?: Responsive<PointerEventsToken>;
  userSelect?: Responsive<UserSelectToken>;
  /** Content clipped by this box dissolves at the named edge instead of
   *  ending on a hard line — a capped block that says "there is more". */
  fade?: Responsive<FadeToken>;
  touchAction?: Responsive<TouchActionToken>;
  snapAlign?: Responsive<SnapAlignToken>;
  snapType?: Responsive<SnapTypeToken>;
  // Motion
  transition?: Responsive<TransitionToken>;
  duration?: Responsive<DurationToken>;
  /**
   * Names this Box as a hover scope, so descendants with the matching
   * `hoverReveal` fade in when it is hovered.
   */
  group?: GroupScope;
  /**
   * `false` keeps a `group` from arming on a held press (`lib/touch-arm`):
   * its hover chrome is pointer-only, so a finger's hold is left to whatever
   * else owns it (a drag) and the release is a plain press. Default `true`.
   */
  armOnHold?: boolean;
  /**
   * Chrome that stays invisible until its `group={scope}` ancestor is hovered
   * (or something inside takes focus, or a popup opens). On a coarse pointer,
   * where hover never fires, it appears when a HELD press ARMS the group
   * (`lib/touch-arm`: a tap presses, a hold reveals) — and it is inert until
   * then, so a blind tap lands on the group, never on an invisible button.
   * Add `revealOnTouch` only where the affordance is the group's whole point
   * and must stand permanently on touch.
   */
  hoverReveal?: GroupScope;
  /**
   * How `hoverReveal` hides the chrome at rest. `opacity` (the default) fades
   * it in and keeps its slot, so the row does not shift; `display` gives the
   * slot back and lays the chrome out as a flex row only while revealed.
   */
  revealMode?: "opacity" | "display";
  revealOnTouch?: boolean;
}

// Generic over the rendered element — `as` IS the type parameter, so
// `as="a"` carries href/target/rel and `as="form"` carries action/method.
export type BoxProps<T extends BoxElement = "div"> = Omittable<
  BoxOwnProps & { as?: T }
> &
  Omit<useRender.ComponentProps<T>, keyof BoxOwnProps | "as">;

/** The scalar behind a prop, or undefined when it is a breakpoint object.
 *  Scalars keep Box's original inline-style path untouched. */
function scalar<T>(value: Responsive<T> | undefined): T | undefined {
  return value != null && !isBreakpointObject(value) ? value : undefined;
}

/** An edge's responsive value, preferring the specific prop over the
 *  shorthand. A scalar on the specific prop suppresses the shorthand here
 *  because inline `style` already carries it, and inline style outranks the
 *  variable rule — which is the same precedence the scalar path has always had. */
function edge(
  specific: Responsive<number | string> | undefined,
  shorthand: Responsive<number | string> | undefined,
): Responsive<number | string> | undefined {
  if (isBreakpointObject(specific)) return specific;
  if (specific != null) return undefined;
  return shorthand;
}

export function Box<T extends BoxElement = "div">({
  as, render, className, style, children,
  direction, align, alignSelf, justify, wrap, gap, gapX, gapY, flex, grow,
  shrink, display, columns,
  padding, paddingX, paddingY, paddingTop, paddingBottom, paddingLeft,
  paddingRight,
  margin, marginX, marginY, marginTop, marginBottom, marginLeft, marginRight,
  marginXNegative, marginYNegative, marginTopNegative, marginBottomNegative,
  marginLeftNegative, marginRightNegative,
  width, height, minWidth, minHeight, maxWidth, maxHeight, fill,
  background, hoverBackground, selected, dimmed, rounded, roundedSide, border, borderColor,
  borderStyle,
  borderWidth, shadow, opacity, surface, surfaceShadow,
  position, inset, insetX, insetY, top, right, bottom, left, zIndex,
  overflow, overflowX, overflowY, scrollbar, hidden, cursor,
  pointerEvents, userSelect, touchAction, snapAlign, snapType, fade,
  transition, duration, group, armOnHold = true, hoverReveal, revealMode = "opacity", revealOnTouch,
  onClick, onPointerDownCapture,
  resize, onResize, onResizeStart, onResizeEnd, resizeLabel, resizeCentered,
  ...props
}: BoxProps<T>): React.ReactElement {
  const tag = as ?? "div";

  // A hover scope is a touch scope (`lib/touch-arm`): a finger held on the
  // group arms it — `data-armed`, read by the `hoverReveal` classes — and the
  // click that release produces is spent on the reveal, so the group's own
  // `onClick` never fires for it and nothing beneath sees it. A tap (released
  // before the hold completes) is the press, exactly as a click. The element
  // is generic over `as`, so its handlers are read at the HTMLElement rung:
  // every tag Box renders is one, and the wrappers touch nothing tag-specific.
  const ownClick = onClick as React.MouseEventHandler<HTMLElement> | undefined;
  const ownPointerDownCapture = onPointerDownCapture as
    | React.PointerEventHandler<HTMLElement>
    | undefined;
  const arms = group != null && armOnHold;
  const handlePointerDownCapture: React.PointerEventHandler<HTMLElement> | undefined =
    arms
      ? (event) => {
          beginHold(event.nativeEvent, event.currentTarget);
          ownPointerDownCapture?.(event);
        }
      : ownPointerDownCapture;
  const handleClick: React.MouseEventHandler<HTMLElement> | undefined = arms
    ? (event) => {
        if (pressArmed(event.currentTarget)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        ownClick?.(event);
      }
    : ownClick;

  // Any flex-family prop opts into flex, and it does so at the BASE breakpoint
  // even when the prop only names a higher one. The alternative leaves
  // `gap={{ md: "sm" }}` inert below md with no error and no visual clue; a
  // caller who genuinely wants block-then-flex writes it out with
  // `display={{ base: "block", md: "flex" }}`.
  const wrapOptsIn =
    wrap === true ||
    (isBreakpointObject(wrap) && Object.values(wrap).some(Boolean));
  const isFlex =
    direction != null || align != null || justify != null || wrapOptsIn ||
    gap != null || gapX != null || gapY != null;

  const displayInput = { display, columns, direction, isFlex };

  // `gap` accepts a token or a raw number, which take different mechanisms.
  // An object form may mix them, so each is filtered out separately.
  const gapToken = filterResponsive<SpacingToken | number, SpacingToken>(gap, isToken);
  const gapXToken = filterResponsive<SpacingToken | number, SpacingToken>(gapX, isToken);
  const gapYToken = filterResponsive<SpacingToken | number, SpacingToken>(gapY, isToken);
  const gapNumber = filterResponsive<SpacingToken | number, number>(gap, isRawNumber);
  const gapXNumber = filterResponsive<SpacingToken | number, number>(gapX, isRawNumber);
  const gapYNumber = filterResponsive<SpacingToken | number, number>(gapY, isRawNumber);

  // The seams anchor to this Box's own edges, so a resizable Box positions
  // itself. Leaving that to the caller is the one mistake that makes a drag
  // silently attach to some ancestor instead.
  const resizeEdges =
    resize == null ? [] : Array.isArray(resize) ? resize : [resize];
  const positionClass =
    position != null
      ? resolve(position, BOX_POSITION)
      : resizeEdges.length > 0
        ? BOX_POSITION.relative
        : undefined;

  const { vars, attribute } = resolveStyleVars({
    w: width, h: height,
    minw: minWidth, minh: minHeight, maxw: maxWidth, maxh: maxHeight,
    inset,
    top: edge(top, insetY), bottom: edge(bottom, insetY),
    left: edge(left, insetX), right: edge(right, insetX),
    z: zIndex,
    gap: gapNumber, gapx: gapXNumber, gapy: gapYNumber,
  });

  const scalarInsetX = scalar(insetX);
  const scalarInsetY = scalar(insetY);
  const inlineStyle: React.CSSProperties = {
    ...(scalar(width) != null && { width: scalar(width) }),
    ...(scalar(height) != null && { height: scalar(height) }),
    ...(scalar(minWidth) != null && { minWidth: scalar(minWidth) }),
    ...(scalar(minHeight) != null && { minHeight: scalar(minHeight) }),
    ...(scalar(maxWidth) != null && { maxWidth: scalar(maxWidth) }),
    ...(scalar(maxHeight) != null && { maxHeight: scalar(maxHeight) }),
    ...(scalar(gapNumber) != null && { gap: scalar(gapNumber) }),
    ...(scalar(gapXNumber) != null && { columnGap: scalar(gapXNumber) }),
    ...(scalar(gapYNumber) != null && { rowGap: scalar(gapYNumber) }),
    ...(scalar(inset) != null && { inset: scalar(inset) }),
    ...(scalarInsetX != null && { left: scalarInsetX, right: scalarInsetX }),
    ...(scalarInsetY != null && { top: scalarInsetY, bottom: scalarInsetY }),
    ...(scalar(top) != null && { top: scalar(top) }),
    ...(scalar(right) != null && { right: scalar(right) }),
    ...(scalar(bottom) != null && { bottom: scalar(bottom) }),
    ...(scalar(left) != null && { left: scalar(left) }),
    ...(scalar(zIndex) != null && { zIndex: scalar(zIndex) }),
    ...vars,
  } as React.CSSProperties;

  const defaultProps = {
    className: cn(
      tag === "button" && BOX_BUTTON_RESET,
      resolveDisplay(displayInput),
      resolve(columns, BOX_COLUMNS),
      resolve(align, BOX_ALIGN),
      resolve(alignSelf, BOX_ALIGN_SELF),
      resolve(justify, BOX_JUSTIFY),
      resolveBoolean(wrap, BOX_WRAP),
      resolve(gapToken, BOX_GAP),
      resolve(gapXToken, BOX_GAP_X),
      resolve(gapYToken, BOX_GAP_Y),
      resolve(flex, BOX_FLEX),
      resolveBoolean(grow, BOX_GROW_TOGGLE),
      resolveBoolean(shrink, BOX_SHRINK, true),
      resolve(padding, BOX_PADDING),
      resolve(paddingX, BOX_PADDING_X),
      resolve(paddingY, BOX_PADDING_Y),
      resolve(paddingTop, BOX_PADDING_TOP),
      resolve(paddingBottom, BOX_PADDING_BOTTOM),
      resolve(paddingLeft, BOX_PADDING_LEFT),
      resolve(paddingRight, BOX_PADDING_RIGHT),
      resolve(margin, BOX_MARGIN),
      resolve(marginX, BOX_MARGIN_X),
      resolve(marginY, BOX_MARGIN_Y),
      resolve(marginTop, BOX_MARGIN_TOP),
      resolve(marginBottom, BOX_MARGIN_BOTTOM),
      resolve(marginLeft, BOX_MARGIN_LEFT),
      resolve(marginRight, BOX_MARGIN_RIGHT),
      resolve(marginXNegative, BOX_MARGIN_X_NEGATIVE),
      resolve(marginYNegative, BOX_MARGIN_Y_NEGATIVE),
      resolve(marginTopNegative, BOX_MARGIN_TOP_NEGATIVE),
      resolve(marginBottomNegative, BOX_MARGIN_BOTTOM_NEGATIVE),
      resolve(marginLeftNegative, BOX_MARGIN_LEFT_NEGATIVE),
      resolve(marginRightNegative, BOX_MARGIN_RIGHT_NEGATIVE),
      resolveWith(fill, (v) => BOX_FILL[v === true ? "true" : v]),
      resolve(background, BOX_BACKGROUND),
      resolveBoolean(hoverBackground, BOX_HOVER_BACKGROUND_TOGGLE),
      selected && BOX_SELECTED,
      dimmed && BOX_DIMMED,
      resolveRounded(rounded, roundedSide),
      resolveBorder(border),
      !border && (borderColor || borderStyle) && "border",
      resolve(borderStyle, BOX_BORDER_STYLE),
      resolve(borderWidth, BOX_BORDER_WIDTH),
      resolve(shadow, BOX_SHADOW),
      // AFTER background/shadow: a Box that names a surface wears that
      // surface's fill, so its classes must out-merge both on the same Box.
      resolve(surface, BOX_SURFACE),
      // AFTER surface: an explicit borderColor is the author overriding the
      // surface's bundled hairline, so it must win (a destructive-bordered
      // well, a page-colored frame on a sidebar pane).
      resolve(borderColor, BOX_BORDER_COLOR),
      resolve(surfaceShadow, BOX_SURFACE_SHADOW),
      resolve(opacity, BOX_OPACITY),
      positionClass,
      resolve(overflow, BOX_OVERFLOW),
      resolve(overflowX, BOX_OVERFLOW_X),
      resolve(overflowY, BOX_OVERFLOW_Y),
      resolve(scrollbar, BOX_SCROLLBAR),
      resolveHidden(hidden, displayInput),
      resolve(cursor, BOX_CURSOR),
      // Before `userSelect`/`touchAction`, so a caller's explicit value on
      // the group Box still wins over the press-surface default.
      group && PRESS_SURFACE_CLASS,
      resolve(pointerEvents, BOX_POINTER_EVENTS),
      resolve(userSelect, BOX_USER_SELECT),
      resolve(touchAction, BOX_TOUCH_ACTION),
      resolve(fade, BOX_FADE),
      resolve(snapAlign, BOX_SNAP_ALIGN),
      resolve(snapType, BOX_SNAP_TYPE),
      resolve(transition, BOX_TRANSITION),
      resolve(duration, BOX_DURATION),
      group && BOX_GROUP[group],
      hoverReveal &&
        (revealMode === "display"
          ? BOX_HOVER_REVEAL_DISPLAY[hoverReveal]
          : BOX_HOVER_REVEAL[hoverReveal]),
      hoverReveal && revealOnTouch && BOX_REVEAL_ON_TOUCH,
      className, // consumer className merges LAST
    ) || undefined,
    style: Object.keys(inlineStyle).length
      ? { ...inlineStyle, ...(style as React.CSSProperties) }
      : style,
    "data-slot": "box",
    onClick: handleClick,
    onPointerDownCapture: handlePointerDownCapture,
    // The chrome names its scope, for tests and tooling that read the DOM.
    ...(hoverReveal ? { [HOVER_REVEAL_ATTR]: hoverReveal } : null),
    ...(hoverReveal && revealOnTouch ? { [REVEAL_ON_TOUCH_ATTR]: "" } : null),
    // Names which properties read a responsive variable, so `box-responsive.css`
    // touches only the Boxes that asked for one.
    ...(attribute ? { "data-box-r": attribute } : null),
    // Rebinds the ink and hairline slots for the subtree, so a descendant's
    // `border-border` / `text-foreground` resolves against THIS surface instead
    // of the page. The fill stays out of it — see the surface-scope block in
    // `tokens.css` for why the asymmetry is deliberate.
    //
    // A responsive surface contributes its base stop: an attribute cannot carry
    // a media query, and the fill it would disagree with is one this Box paints
    // on itself either way.
    ...(surface ? { "data-on": at(surface, "base") } : null),
    ...(tag === "button" && !render ? { type: "button" as const } : null),
    children:
      resizeEdges.length === 0 ? (
        children
      ) : (
        <>
          {resizeEdges.map((edgeName) => (
            <BoxResizeSeam
              centered={resizeCentered}
              edge={edgeName}
              key={edgeName}
              label={
                resizeEdges.length > 1
                  ? `${resizeLabel ?? "Resize"} (${edgeName})`
                  : (resizeLabel ?? "Resize")
              }
              onResize={onResize}
              onResizeEnd={onResizeEnd}
              onResizeStart={onResizeStart}
            />
          ))}
          {children}
        </>
      ),
  };

  return useRender({
    defaultTagName: tag,
    props: mergeProps<"div">(
      defaultProps,
      props as useRender.ComponentProps<"div">,
    ),
    render,
  });
}
